import React from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MultiSelectDropdown from '../components/MultiSelectDropdown';

const ComparisonFilters = ({
  nations,
  alliances,
  commanders,
  selectedNations,
  onNationsChange,
  selectedAlliances,
  onAlliancesChange,
  selectedCommanders,
  onCommandersChange,
  onApply,
  loadingOptions,
  loadingScopedOptions,
  applying
}) => {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      alignItems={{ xs: 'stretch', sm: 'flex-end' }}
      className="comparison-filters"
    >
      <MultiSelectDropdown
        label="Nations"
        options={nations}
        selectedValues={selectedNations}
        onChange={onNationsChange}
        getOptionLabel={(option) => option.number?.toString() ?? ''}
        getOptionValue={(option) => option._id}
        loading={loadingOptions}
        maxSelections={4}
        helperText="Select up to 4 nations"
        filterLimit={200}
      />

      <MultiSelectDropdown
        label="Alliances"
        options={alliances}
        selectedValues={selectedAlliances}
        onChange={onAlliancesChange}
        getOptionLabel={(option) =>
          option.acronym
            ? `${option.acronym} (${option.nation?.number ?? 'N/A'})`
            : option.name ?? ''
        }
        getOptionValue={(option) => option._id}
        loading={loadingOptions || loadingScopedOptions}
        disabled={!selectedNations.length}
        helperText={
          selectedNations.length
            ? 'Search or select alliances'
            : 'Select nations first'
        }
        filterLimit={200}
      />

      <MultiSelectDropdown
        label="Commanders"
        options={commanders}
        selectedValues={selectedCommanders}
        onChange={onCommandersChange}
        getOptionLabel={(option) => {
          const nationNumber = option.nation?.number ?? 'N/A';
          const acronym = option.alliance?.acronym ?? 'N/A';
          const battlePower = Number(option.battlePower || 0).toLocaleString();
          return `#${nationNumber} (${acronym}) ${option.name ?? 'Unknown'} - ${battlePower}`;
        }}
        getOptionValue={(option) => option._id}
        loading={loadingOptions || loadingScopedOptions}
        disabled={!selectedNations.length}
        helperText={
          selectedNations.length
            ? 'Search or select commanders'
            : 'Select nations first'
        }
        filterLimit={200}
      />

      <Stack spacing={0.5}>
        <Button
          variant="contained"
          onClick={onApply}
          disabled={!selectedNations.length || applying}
        >
          {applying ? 'Applying...' : 'Apply Filters'}
        </Button>
        {!selectedNations.length && (
          <Typography variant="caption" color="text.secondary">
            Choose at least one nation.
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

ComparisonFilters.propTypes = {
  nations: PropTypes.arrayOf(PropTypes.object).isRequired,
  alliances: PropTypes.arrayOf(PropTypes.object).isRequired,
  commanders: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedNations: PropTypes.arrayOf(PropTypes.object).isRequired,
  onNationsChange: PropTypes.func.isRequired,
  selectedAlliances: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAlliancesChange: PropTypes.func.isRequired,
  selectedCommanders: PropTypes.arrayOf(PropTypes.object).isRequired,
  onCommandersChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  loadingOptions: PropTypes.bool.isRequired,
  loadingScopedOptions: PropTypes.bool.isRequired,
  applying: PropTypes.bool.isRequired
};

export default ComparisonFilters;
